// Per-member Reels preferences persisted in localStorage:
//   • volume choice (sound / muted) — remembered across sessions
//   • playback position per reel — resume where the member left off
// All helpers are best-effort and never throw (private mode / quota safe).
import { isPositionFresh, onPositionMemoryExpire } from "@/lib/positionMemory";

const MUTED_KEY = "yalola.reels.muted";
const PROGRESS_KEY = "yalola.reels.progress";
const PROGRESS_MAX = 200; // cap stored positions to avoid unbounded growth

// --- Volume memory --------------------------------------------------------
// Returns true (muted) / false (sound on) / null (no saved choice yet).
export const readMutedPref = () => {
  try {
    const v = localStorage.getItem(MUTED_KEY);
    return v === null ? null : v === "1";
  } catch {
    return null;
  }
};

export const writeMutedPref = (muted) => {
  try { localStorage.setItem(MUTED_KEY, muted ? "1" : "0"); } catch { /* noop */ }
};

// --- Resume playback ------------------------------------------------------
const _readProgress = () => {
  try { return JSON.parse(localStorage.getItem(PROGRESS_KEY) || "{}") || {}; }
  catch { return {}; }
};

export const getReelProgress = (id) => {
  if (!id) return 0;
  const e = _readProgress()[id];
  if (!e || typeof e.t !== "number") return 0;
  // Délai de mémorisation (admin) dépassé → on repart du début.
  if (!isPositionFresh(e.at)) return 0;
  // Jamais près de la fin (< 3 s) : on repart du début plutôt que de voir 2 s puis la boucle.
  if (typeof e.d === "number" && e.d > 0 && e.t >= e.d - 3) return 0;
  return e.t;
};

export const saveReelProgress = (id, t, duration) => {
  if (!id || typeof t !== "number" || t < 1) return;
  const map = _readProgress();
  // Finished / looped back to (near) the end → don't resume, clear the entry.
  if (duration && t >= duration - 1) {
    if (id in map) { delete map[id]; try { localStorage.setItem(PROGRESS_KEY, JSON.stringify(map)); } catch { /* noop */ } }
    return;
  }
  map[id] = { t, d: duration || undefined, at: Date.now() };
  const keys = Object.keys(map);
  if (keys.length > PROGRESS_MAX) {
    keys.sort((a, b) => (map[a].at || 0) - (map[b].at || 0));
    for (const k of keys.slice(0, keys.length - PROGRESS_MAX)) delete map[k];
  }
  try { localStorage.setItem(PROGRESS_KEY, JSON.stringify(map)); } catch { /* noop */ }
};

// --- Dernier reel vu ------------------------------------------------------
// L'onglet Reels rouvre sur le dernier reel regardé (s'il est encore dans le flux et vu il y a moins que
// le délai de mémorisation réglé par l'admin — positionMemory).
const LAST_KEY = "yalola.reels.last";

export const saveLastReel = (id) => {
  if (!id) return;
  try { localStorage.setItem(LAST_KEY, JSON.stringify({ id, at: Date.now() })); } catch { /* noop */ }
};

export const readLastReel = () => {
  try {
    const e = JSON.parse(localStorage.getItem(LAST_KEY) || "null");
    if (!e?.id || !isPositionFresh(e.at)) return null;
    return e.id;
  } catch {
    return null;
  }
};

// Délai de mémorisation dépassé : dernier reel vu + positions de lecture oubliés.
onPositionMemoryExpire(() => {
  try { localStorage.removeItem(LAST_KEY); localStorage.removeItem(PROGRESS_KEY); } catch { /* noop */ }
});

// Filtre « Ma langue » : mémorisé pour que la reprise rouvre le MÊME flux que celui qu'on a quitté.
const MY_LANG_KEY = "yalola.reels.myLangOnly";
export const readMyLangOnly = () => {
  try { const v = localStorage.getItem(MY_LANG_KEY); return v === null ? true : v === "1"; } catch { return true; }
};
export const writeMyLangOnly = (on) => {
  try { localStorage.setItem(MY_LANG_KEY, on ? "1" : "0"); } catch { /* noop */ }
};
